import * as React from 'react';
import {useMoneyFormatter} from 'src/hooks/useUnits';
import {cn} from 'src/lib/utils';
import type Money from 'common/Money';

/**
 * - `signed` (default): income/positive renders green; spending/negative
 *   stays in the surrounding foreground color (the minus sign already
 *   carries direction). For transaction amounts in lists and headers.
 * - `plain`: never colored. For balances, totals, and amounts inline in
 *   prose where sign coloring is noise.
 * - `delta`: green when positive, destructive when negative. For changes
 *   between two values, where both directions are meaningful.
 */
type Variant = 'signed' | 'plain' | 'delta';

type Props = {
  amount: Money;
  variant?: Variant;
  inline?: boolean;
  align?: 'right' | 'left';
  /**
   * Home-currency (USD) equivalent, rendered as a muted "= $x" line under
   * the amount. Strict equals, not "≈": the linked conversion is the
   * definitive value of foreign units in this model — the market rate is an
   * unrelated fact — so the drawn basis is exactly what the amount is
   * worth. Pass only for foreign-currency amounts; null and undefined
   * render nothing.
   */
  approx?: Money | null;
};

export const AmountDisplay = (props: Props) => {
  const {amount, variant = 'signed', align = 'right', inline = false} = props;
  const approx = props.approx ?? null;
  const formatMoney = useMoneyFormatter();

  const formatted = formatMoney(amount);
  return (
    <div
      className={cn(
        align === 'right' ? 'text-right' : 'text-left',
        inline ? 'inline-block' : 'block',
      )}
    >
      <div
        className={cn(
          'tabular-nums',
          variant !== 'plain' &&
            amount.isPositive &&
            'text-positive',
          variant === 'delta' && amount.isNegative && 'text-destructive',
        )}
      >
        {formatted}
      </div>
      {approx !== null && (
        <div className="text-muted-foreground text-xs tabular-nums">
          = {formatMoney(approx)}
        </div>
      )}
    </div>
  );
};
